/** WHAT THE ACCOUNT PAGE IS MADE OF, named once.
 *
 *  The account page is a set of sections addressed as `/account?cat=<id>`. Two surfaces have to agree on
 *  that set: the deck's own navigation, which draws one row per section, and the page itself, which
 *  mounts the matching panel and titles its hero. They used to be split — the sidebar drew the sections
 *  as its sub-items while the page carried a rail of its own — and the sidebar holds one row for the deck
 *  now, so the list lives inside the view and both shapes of the navigation are drawn from it.
 *
 *  Plugin-contributed sections are handed IN rather than resolved here: they come from two live queries
 *  and belong to whoever already holds them. What this module owns is the core sections, their labels,
 *  and the one order every surface draws. */
import { Bell, Boxes, Brain, KeyRound, Laptop, Mic, Settings2, Sparkles, SquareTerminal, UserCog, Vault, Webhook, type LucideIcon } from 'lucide-react';
import type { LocaleDict } from '../../lib/i18n/types';
import type { PluginUiListing, UserPluginConfigDetail } from '../../lib/types';
import { pluginLucideIcon } from '../../lib/pluginIcons';
import { parsePluginAccountSectionId, parsePluginUserConfigSectionId, pluginAccountSectionId, pluginUserConfigSectionId } from './pluginSections';
import { userPluginConfigDescription, userPluginConfigLabel } from './userPluginConfigStrings';

/** The ids this page answers for. Not exported: what a caller needs is `isAccountSection` to validate one
 *  and `accountSections` to draw them, and a second list of the same names is a second thing to update. */
const CORE_ACCOUNT_SECTIONS = ['profile', 'security', 'notifications', 'personality', 'cli', 'terminal', 'memory', 'voice', 'vault', 'apiTokens', 'elowenCli'] as const;
type CoreAccountSection = typeof CORE_ACCOUNT_SECTIONS[number];
export type AccountSection = CoreAccountSection | `plugin-account:${string}` | `plugin-user-config:${string}`;

/** Whether a stored or linked id names a section at all. Shape only — whether the plugin behind a
 *  `plugin-*` id is still installed is a question for the listing, and the page asks it separately. */
export const isAccountSection = (value: string): value is AccountSection =>
  (CORE_ACCOUNT_SECTIONS as readonly string[]).includes(value)
  || parsePluginAccountSectionId(value) !== null
  || parsePluginUserConfigSectionId(value) !== null;

export interface AccountSectionDescriptor {
  id: AccountSection;
  icon: LucideIcon;
  label: string;
  description: string;
  /** Place in the menu, on the one scale core sections and plugin panels share. */
  order: number;
  /** The contributing plugin's name; absent for a core section. Breaks ties between equal orders. */
  pluginName?: string;
}

/** The menu's scale. Core sections are spaced by 100 so a plugin can slot between any two. A plugin panel
 *  without a manifest `order` takes `PLUGIN_PANEL_ORDER`, right after Profile, where every plugin panel
 *  sat before the field existed; a plugin's per-account configuration form, which has no manifest entry
 *  of its own, takes `USER_CONFIG_ORDER`, after them. Computers follows Sandbox's Desktops (250). */
const PLUGIN_PANEL_ORDER = 200;
const USER_CONFIG_ORDER = 300;

/** Equal orders never swap between loads: core before plugin, then plugin name, then section id, both
 *  compared by code point so the process locale cannot change the result. */
const byCodePoint = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);
function compareSections(a: AccountSectionDescriptor, b: AccountSectionDescriptor): number {
  if (a.order !== b.order) return a.order - b.order;
  if ((a.pluginName === undefined) !== (b.pluginName === undefined)) return a.pluginName === undefined ? -1 : 1;
  return byCodePoint(a.pluginName ?? '', b.pluginName ?? '') || byCodePoint(a.id, b.id);
}

/** Where a section of the account page is addressed. The one rule, so a menu row and a cross-link cannot
 *  disagree about the query parameter or spell the route two ways.
 *
 *  Encoded because a plugin-contributed id carries colons, and the page writes the same address back with
 *  `URLSearchParams`, which encodes them. Both spellings decode to the same section, but two spellings of
 *  one address in the bar is a difference someone eventually reads as a difference. */
export const accountSectionHref = (id: string): string => `/account?cat=${encodeURIComponent(id)}`;

/** Every section, in draw order: who you are, then whatever the installed plugins contribute (Computers
 *  after Sandbox's Desktops), then the rest of the personal settings — the runtime and what shapes it,
 *  the operational pair, and the cosmetic terminal last. One sort over core and contributed sections. */
export function accountSections(t: LocaleDict, contributed: readonly AccountSectionDescriptor[]): AccountSectionDescriptor[] {
  const core: AccountSectionDescriptor[] = [
    { id: 'profile', icon: UserCog, label: t.account.tabProfile, description: t.account.profileHint, order: 100 },
    { id: 'elowenCli', icon: Laptop, label: t.account.tabComputers, description: t.elowenCli.sectionHint, order: 260 },
    { id: 'cli', icon: Boxes, label: t.account.tabCli, description: t.cli.modelRolesHint, order: 400 },
    { id: 'voice', icon: Mic, label: t.voice.accountSection, description: t.voice.accountHint, order: 500 },
    { id: 'memory', icon: Brain, label: t.account.tabMemory, description: t.help.memoryRecall, order: 600 },
    { id: 'personality', icon: Sparkles, label: t.account.tabPersonality, description: t.personality.intro, order: 700 },
    { id: 'notifications', icon: Bell, label: t.account.tabNotifications, description: t.help.pushEnable, order: 800 },
    { id: 'security', icon: KeyRound, label: t.account.tabSecurity, description: t.account.passwordHint, order: 900 },
    { id: 'vault', icon: Vault, label: t.vault.title, description: '', order: 950 },
    { id: 'apiTokens', icon: Webhook, label: t.account.tabApiTokens, description: t.apiTokens.sectionHint, order: 1000 },
    { id: 'terminal', icon: SquareTerminal, label: t.account.tabTerminal, description: t.terminal.colorsHelp, order: 1100 },
  ];
  return [...core, ...contributed].sort(compareSections);
}

/** A section a plugin contributes to the account page, with what the page needs in order to mount it. */
export interface PluginAccountSectionEntry extends AccountSectionDescriptor {
  plugin: PluginUiListing;
  sectionId: string;
  /** `linkedAccount` hangs the panel in the profile's Linked accounts drawer instead of claiming a
   *  section of its own. Absent means `section`: a daemon too old to send the field keeps its entry. */
  placement: string;
}

/** The account sections declared by the installed plugins, from the same listing the menu already holds.
 *
 *  The label is the plugin's own name for the section and the description is its sentence — two different
 *  manifest strings, resolved here so a menu row and a hero can never disagree about which is which. */
export function pluginAccountSectionEntries(listing: readonly PluginUiListing[]): PluginAccountSectionEntry[] {
  return listing.flatMap((entry) => (entry.account ?? []).map((account) => ({
    id: pluginAccountSectionId(entry.name, account.id),
    plugin: entry,
    sectionId: account.id,
    placement: account.placement ?? 'section',
    icon: pluginLucideIcon(account.icon),
    label: account.label,
    description: entry.strings?.accountHint ?? entry.label ?? account.label,
    order: account.order ?? PLUGIN_PANEL_ORDER,
    pluginName: entry.name,
  })));
}

/** The sections a plugin's PER-ACCOUNT configuration claims. A different contribution from the one above:
 *  those are surfaces a bundle draws, these are schema-driven forms the host renders for it. */
export interface UserPluginConfigSectionEntry extends AccountSectionDescriptor {
  detail: UserPluginConfigDetail;
}

export function userPluginConfigSectionEntries(
  configs: readonly UserPluginConfigDetail[],
  locale: string,
  fallbackDescription: string,
): UserPluginConfigSectionEntry[] {
  // A plugin whose own page already presents these values (manifest `userConfigPlacement: 'pluginPage'`)
  // gets no rail entry here: two editors for one setting is how the two drift, and the plugin's page can
  // show what a schema-driven form cannot — what each value belongs to, and when a stored pick went stale.
  return configs.filter((detail) => detail.placement !== 'pluginPage').map((detail) => ({
    id: pluginUserConfigSectionId(detail.name),
    detail,
    icon: Settings2,
    label: userPluginConfigLabel(detail, locale),
    description: userPluginConfigDescription(detail, locale) ?? fallbackDescription,
    order: USER_CONFIG_ORDER,
    pluginName: detail.name,
  }));
}
